import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Heart, Mail, Calendar, ArrowRight, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

interface ThankYouPageProps {
  type?: 'donation' | 'subscription';
  supporterId?: string;
}

export function ThankYouPage({ type = 'donation', supporterId }: ThankYouPageProps) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'LOADING' | 'SUCCESS' | 'ERROR' | 'CANCELED' | 'PENDING'>('LOADING');
  const [orderId, setOrderId] = useState<string | null>(null);
  
  const isSubscription = type === 'subscription';

  useEffect(() => {
    // 1. Sprawdzamy błędy w URL
    const errorParam = searchParams.get('error');
    if (errorParam) {
        setStatus('ERROR');
        return;
    }

    // 2. Pobieramy ID z URL (to może być donation_... albo id od PayU)
    let urlId = searchParams.get('orderId') || searchParams.get('id');

    // 3. --- FIX DLA BŁĘDU 405 (LOCALSTORAGE) ---
    // Sprawdzamy, czy w kieszeni mamy zapisane prawdziwe ID z PayU
    const cachedPayuId = localStorage.getItem('lastPayuOrderId');
    
    console.log(`🔍 Weryfikacja: URL_ID=${urlId}, CACHED_ID=${cachedPayuId}`);

    // Jeśli ID z URL to 'donation_...', a my mamy w schowku prawdziwe ID PayU
    // to podmieniamy je, żeby backend dostał to, co lubi (czyli ID PayU)
    if (urlId && urlId.startsWith('donation_') && cachedPayuId) {
        console.log('🔄 Podmieniam ID donation_... na ID PayU z pamięci przeglądarki');
        urlId = cachedPayuId;
    }
    // -------------------------------------------

    const finalId = urlId;
    
    if (!finalId) {
      setStatus(supporterId ? 'SUCCESS' : 'ERROR');
      return;
    }

    setOrderId(finalId);
    checkOrderStatus(finalId);
  }, [searchParams, supporterId]);

  const checkOrderStatus = async (id: string) => {
    try {
      // 👇 Pobieramy email ze schowka
      const email = localStorage.getItem('lastPayuEmail') || '';
      const consent = localStorage.getItem('lastPayuNewsletterConsent') || 'true';

      const response = await fetch(
        '/.netlify/functions/payu-get-status?orderId=' + id + '&email=' + email + '&newsletterConsent=' + consent
      );
      const data = await response.json();
      console.log('Status PayU:', data.status);

      if (data.status === 'COMPLETED') {
        setStatus('SUCCESS');
      } else if (data.status === 'CANCELED') {
        setStatus('CANCELED');
      } else if (['PENDING', 'NEW', 'WAITING_FOR_CONFIRMATION'].includes(data.status)) {
        setStatus('PENDING');
      } else {
        setStatus('ERROR');
      }
    } catch (error) {
      console.error('Błąd weryfikacji:', error);
      setStatus('ERROR');
    }
  };

  // --- KONFIGURACJA WIDOKU W ZALEŻNOŚCI OD STATUSU ---
  // To pozwala zachować czysty JSX i łatwo zmieniać kolory/teksty
  const config = {
    LOADING: {
      icon: <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />,
      bg: 'bg-blue-100',
      title: 'Weryfikacja płatności...',
      description: 'Prosimy o chwilę cierpliwości, łączymy się z bankiem.',
      statusText: 'Weryfikacja...',
      statusColor: 'text-blue-600'
    },
    SUCCESS: {
      icon: <CheckCircle2 className="w-10 h-10 text-green-600" />,
      bg: 'bg-green-100',
      title: 'Dziękujemy za wsparcie!',
      description: isSubscription 
        ? 'Twoja subskrypcja została aktywowana. Regularne wsparcie pomaga nam planować opiekę nad zwierzętami.'
        : 'Twoja darowizna została przekazana. Każda złotówka ma znaczenie dla naszych podopiecznych.',
      statusText: 'Zakończone pomyślnie',
      statusColor: 'text-green-600'
    },
    PENDING: {
      icon: <AlertCircle className="w-10 h-10 text-yellow-600" />,
      bg: 'bg-yellow-100',
      title: 'Płatność w trakcie księgowania',
      description: 'Twoja wpłata została przyjęta i oczekuje na ostateczne potwierdzenie z banku. Otrzymasz email, gdy tylko proces się zakończy.',
      statusText: 'Oczekiwanie na potwierdzenie',
      statusColor: 'text-yellow-600'
    },
    CANCELED: {
      icon: <XCircle className="w-10 h-10 text-red-600" />,
      bg: 'bg-red-100',
      title: 'Płatność anulowana',
      description: 'Transakcja została anulowana lub odrzucona. Środki nie zostały pobrane.',
      statusText: 'Anulowano',
      statusColor: 'text-red-600'
    },
    ERROR: {
      icon: <XCircle className="w-10 h-10 text-red-600" />,
      bg: 'bg-red-100',
      title: 'Wystąpił błąd',
      description: 'Nie udało się zweryfikować statusu płatności lub wystąpił błąd połączenia.',
      statusText: 'Błąd weryfikacji',
      statusColor: 'text-red-600'
    }
  };

  const currentView = config[status];

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <Helmet>
        <title>{currentView.title} | Fundacja "Znajdki"</title>
        <meta name="description" content={currentView.description} />
      </Helmet>

      {/* --- GŁÓWNY NAGŁÓWEK --- */}
      <div className="text-center mb-12">
        <div className={`w-20 h-20 ${currentView.bg} rounded-full flex items-center justify-center mx-auto mb-6`}>
          {currentView.icon}
        </div>
        
        <h1 className="heading-1 mb-4">
          {currentView.title}
        </h1>
        
        <p className="body-large text-accessible-gray-600 max-w-2xl mx-auto">
          {currentView.description}
        </p>
      </div>

      {/* --- SEKKCJA STATUSU I SZCZEGÓŁÓW --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        
        {/* Co dalej? - Wyświetlamy tylko przy sukcesie lub pending */}
        {(status === 'SUCCESS' || status === 'PENDING') && (
          <div className="bg-white dark:bg-accessible-gray-800 p-8 rounded-lg shadow-sm">
            <h2 className="heading-3 mb-6">Co dalej?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-accessible-red-600 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-medium mb-1">Potwierdzenie email</div>
                  <div className="text-sm text-accessible-gray-600">
                    Wysłaliśmy potwierdzenie na Twój adres email z szczegółami transakcji.
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-accessible-red-600 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-medium mb-1">Newsletter</div>
                  <div className="text-sm text-accessible-gray-600">
                    {isSubscription 
                      ? 'Będziesz otrzymywać regularne aktualizacje o zwierzętach i działaniach fundacji.'
                      : 'Możesz zapisać się do naszego newslettera, aby być na bieżąco z naszymi działaniami.'
                    }
                  </div>
                </div>
              </div>

              {isSubscription && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-accessible-red-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-medium mb-1">Następna płatność</div>
                    <div className="text-sm text-accessible-gray-600">
                      Następna płatność zostanie pobrana automatycznie za miesiąc.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sekcja błędów - co zrobić jak nie wyszło? */}
        {(status === 'CANCELED' || status === 'ERROR') && (
           <div className="bg-white dark:bg-accessible-gray-800 p-8 rounded-lg shadow-sm border border-red-100">
             <h2 className="heading-3 mb-6">Co się stało?</h2>
             <p className="text-accessible-gray-600 mb-4">
               Jeśli anulowałeś płatność celowo, możesz zignorować ten komunikat. Jeśli wystąpił błąd, spróbuj ponownie.
             </p>
             <Link
                to="/wplata-jednorazowa" // Tu wstaw link do swojego formularza wpłaty
                className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors w-full justify-center"
              >
                <ArrowRight className="w-4 h-4" />
                Spróbuj wpłacić ponownie
              </Link>
           </div>
        )}

        {/* Szczegóły transakcji */}
        <div className="bg-accessible-red-50 dark:bg-accessible-red-900/20 p-8 rounded-lg h-fit">
          <h2 className="heading-3 mb-6">Szczegóły transakcji</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Typ wsparcia:</span>
              <span className="font-medium">
                {isSubscription ? 'Subskrypcja miesięczna' : 'Darowizna jednorazowa'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Status:</span>
              <span className={`font-medium ${currentView.statusColor}`}>
                {currentView.statusText}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Data:</span>
              <span className="font-medium">{new Date().toLocaleDateString('pl-PL')}</span>
            </div>

            {(orderId || supporterId) && (
              <div className="flex justify-between">
                <span>ID zamówienia:</span>
                <span className="font-medium text-xs">{orderId || supporterId}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- PRZYCISKI AKCJI --- */}
      <div className="text-center space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            Powrót do strony głównej
          </Link>
          
          <Link
            to="/zwierzeta"
            className="inline-flex items-center gap-2 border border-accessible-gray-300 dark:border-accessible-gray-600 px-6 py-3 rounded-lg hover:bg-accessible-gray-50 dark:hover:bg-accessible-gray-700 transition-colors"
          >
            Zobacz nasze zwierzęta
          </Link>
        </div>

        {/* Sekcja Social Media - tylko przy sukcesie, żeby nie irytować przy błędzie */}
        {status === 'SUCCESS' && (
          <div className="mt-8 p-6 bg-accessible-gray-50 dark:bg-accessible-gray-800 rounded-lg">
            <h3 className="heading-4 mb-3">Chcesz zrobić więcej?</h3>
            <p className="body-medium text-accessible-gray-600 mb-4">
              Możesz podzielić się naszą stroną z przyjaciółmi lub śledzić nas w mediach społecznościowych.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Udostępnij na Facebooku
              </button>
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Udostępnij na WhatsApp
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}